/**
 * @tt/lobby/shop — Embers shop catalog. The Embers shop sells
 * common-rarity cosmetics for the soft currency. No real-money items
 * exist in this catalog; that's intentional and part of the project's
 * "no payments" rule.
 *
 * Each item has a stable `id`, the inventory bucket + value pair to
 * append on purchase, and the price in Embers. The server validates
 * the spend in `lambdas/characters/src/index.ts` (POST /shop/buy).
 */

export type ShopBucket =
  | 'hats' | 'bodies' | 'faces' | 'hues' | 'accentHues' | 'titles'
  | 'emotes' | 'seatBorders' | 'seatBackgrounds' | 'seatParticles' | 'seatBgHues'
  | 'palettes';

export interface ShopItem {
  id: string;
  label: string;
  description: string;
  bucket: ShopBucket;
  ref: string | number;
  price: number;
  rarity: 1 | 2 | 3;
  /** Always 'shop' — sentinel for the dedup test in
   *  customization-updates.md §4. */
  source: 'shop';
}

/** Embers shop catalog. Prices match the rarity-tier guideline AFTER the
 *  R2#74 rebalance:
 *    r1 → 300–400 (was 150–200)
 *    r2 → 600–1000 (was 300–500)
 *    r3 → 1500–2000 (was 750–1000)
 *  Doubled to track the new ~2k-E/week earn rate post-#29. Lanterns /
 *  r4 are intentionally excluded — those come from the season pass.
 *
 *  STRICT RULE (customization-updates.md §4): every item here must NOT
 *  appear in `evaluateUnlocks` or as an achievement reward. The
 *  `tools/validate-catalog.mjs` script enforces this on every commit.
 *  Adding an overlapping item will fail the build. Players who already
 *  bought items that were once in the shop and have since moved out
 *  KEEP them — the inventory is sticky. */
export const EMBERS_SHOP: ShopItem[] = [
  // Hue swatches — common, easy starter purchases. Indices chosen to
  // NOT collide with `evaluateUnlocks` hues (which has 30/60/90/140/
  // 170/210/270/290/330/350). Shop-exclusive hues: 50 (honey), 110
  // (mint), 240 (indigo), 320 (orchid).
  { id: 'hue-honey',   label: 'Honey hue',   description: 'Body color in honey gold.',     bucket: 'hues', ref: 50,  price: 300, rarity: 1, source: 'shop' },
  { id: 'hue-mint',    label: 'Mint hue',    description: 'Body color in cool mint.',      bucket: 'hues', ref: 110, price: 300, rarity: 1, source: 'shop' },
  { id: 'hue-indigo',  label: 'Indigo hue',  description: 'Body color in deep indigo.',    bucket: 'hues', ref: 240, price: 400, rarity: 1, source: 'shop' },
  { id: 'hue-orchid',  label: 'Orchid hue',  description: 'Body color in soft orchid.',    bucket: 'hues', ref: 320, price: 400, rarity: 1, source: 'shop' },
  // Seat background tavern is shop-exclusive (not in evaluateUnlocks).
  { id: 'seat-bg-tavern',  label: 'Tavern glow seat',   description: 'Warm amber backdrop on your lobby tile.', bucket: 'seatBackgrounds', ref: 'tavern',  price: 500, rarity: 2, source: 'shop' },
  // Seat bg hue extras at indices NOT in evaluateUnlocks (which has
  // 0/90/140/180/250/330). Shop-exclusive: 25 (ember), 290 (velvet).
  { id: 'seat-hue-ember',  label: 'Ember seat hue',     description: 'Seat tile gradient in warm ember.',      bucket: 'seatBgHues',      ref: 25,        price: 700, rarity: 2, source: 'shop' },
  { id: 'seat-hue-velvet', label: 'Velvet seat hue',    description: 'Seat tile gradient in dusk velvet.',     bucket: 'seatBgHues',      ref: 290,       price: 700, rarity: 2, source: 'shop' },
  // Emotes — Bolt + Dice are now shop-only (removed from the play
  // catalog per §4.3 — players who only play earn a different set of
  // emotes via achievements).
  { id: 'emote-bolt',      label: 'Bolt emote',         description: 'Lightning bolt reaction for the picker.', bucket: 'emotes',          ref: '⚡',     price: 400, rarity: 1, source: 'shop' },
  { id: 'emote-dice',      label: 'Dice emote',         description: 'Dice reaction for the picker.',           bucket: 'emotes',          ref: '🎲',     price: 400, rarity: 1, source: 'shop' },
  // New shop-exclusive title.
  { id: 'title-sapphire-patron', label: 'Sapphire Patron title', description: 'A blue-tinted Patron title pinned to your seat.', bucket: 'titles', ref: 'Sapphire Patron', price: 2000, rarity: 3, source: 'shop' },
  // Storm emote — shop-exclusive r3 reaction.
  { id: 'emote-storm',     label: 'Storm emote',        description: 'A storm-cloud reaction for the picker.', bucket: 'emotes',          ref: '⛈️',     price: 1200, rarity: 3, source: 'shop' },
  // Palette: 3-color override (Phase 2). Equip via Locker palette slot.
  { id: 'palette-dusk',    label: 'Dusk palette',       description: 'A 3-color override: deep purple body, rose accents, gold features.', bucket: 'palettes', ref: 'palette-dusk', price: 1000, rarity: 2, source: 'shop' },

  // ── R2#74 catalog expansion — 12 new items spread across rarities.
  // Each entry below is verified against unlocks.ts + ACHIEVEMENTS_V2
  // rewards by hand (re-checked via tools/validate-catalog.mjs on
  // commit). Keeping them grouped at the bottom so future audits can
  // see the new additions at a glance.
  //
  // Hue swatches (open numeric range, just need values not in
  // unlocks.ts/defaults/existing shop):
  { id: 'hue-rose-petal', label: 'Rose petal hue', description: 'Body color in soft rose petal.',  bucket: 'hues', ref: 5,   price: 300, rarity: 1, source: 'shop' },
  { id: 'hue-aqua',       label: 'Aqua hue',       description: 'Body color in clear aqua.',       bucket: 'hues', ref: 195, price: 350, rarity: 1, source: 'shop' },
  { id: 'hue-marigold',   label: 'Marigold hue',   description: 'Body color in marigold gold.',    bucket: 'hues', ref: 75,  price: 400, rarity: 1, source: 'shop' },
  { id: 'hue-grape',      label: 'Grape hue',      description: 'Body color in deep grape.',       bucket: 'hues', ref: 280, price: 600, rarity: 2, source: 'shop' },
  // Emotes (open string codepoints; existing shop emotes are
  // ⚡ 🎲 ⛈️; ach-rewarded are ✨ 💀; play-unlocked are 🏆 💎 🍀 🎭 🍺):
  { id: 'emote-target',         label: 'Target emote',         description: 'A bullseye reaction for the picker.',     bucket: 'emotes', ref: '🎯', price: 350, rarity: 1, source: 'shop' },
  { id: 'emote-flame',          label: 'Flame emote',          description: 'A flame reaction for the picker.',         bucket: 'emotes', ref: '🔥', price: 700, rarity: 2, source: 'shop' },
  { id: 'emote-cheers',         label: 'Cheers emote',         description: 'A toast reaction for the picker.',         bucket: 'emotes', ref: '🥂', price: 800, rarity: 2, source: 'shop' },
  { id: 'emote-shooting-star',  label: 'Shooting Star emote',  description: 'A shooting star reaction for the picker.', bucket: 'emotes', ref: '💫', price: 1000, rarity: 2, source: 'shop' },
  { id: 'emote-dragon',         label: 'Dragon emote',         description: 'A dragon reaction for the picker.',        bucket: 'emotes', ref: '🐉', price: 1800, rarity: 3, source: 'shop' },
  // Titles (open strings; ach-rewarded titles are listed in ACH_V2 +
  // every perGameMilestone helper. None of the names below are claimed):
  { id: 'title-tavern-patron',  label: 'Tavern Patron title',  description: 'A bronze patron title pinned to your seat.', bucket: 'titles', ref: 'Tavern Patron',  price: 900,  rarity: 2, source: 'shop' },
  { id: 'title-ruby-patron',    label: 'Ruby Patron title',    description: 'A ruby-tinted patron title pinned to your seat.', bucket: 'titles', ref: 'Ruby Patron',    price: 1500, rarity: 3, source: 'shop' },
  { id: 'title-emerald-patron', label: 'Emerald Patron title', description: 'An emerald-tinted patron title pinned to your seat.', bucket: 'titles', ref: 'Emerald Patron', price: 2000, rarity: 3, source: 'shop' },
  // Note: hat-top (idx 3), hat-flower (idx 6), hue-azure (210),
  // hue-velvet (290), seat-snow, seat-cards, seat-hue-honey (50),
  // seat-hue-tide (180), seat-frame-frost, seat-frame-storm USED to
  // live here. All overlapped with play unlocks. Removed in the
  // §4 split. Players who bought any of them keep the item.
];

/** Look up a shop item by id, or null if missing. */
export function findShopItem(id: string): ShopItem | null {
  return EMBERS_SHOP.find((it) => it.id === id) ?? null;
}
